<script>
import { useContextStore } from "@/store/context";
import { useMagasinStore } from "@/store/magasin";
import { mapActions, mapState } from "pinia";
import { LayoutTypes } from "@/services/context/layout-helper";
import productInfoApiDrive from "@/services/http/api-drive/magasin/produit/productInfo";
import avisVerifiesCodes from "@/utils/avis-verifies-codes";
import productReviewsApiDrive from "@/services/http/api-drive/magasin/produit/reviews";
import StateOverlay from "@/components/molecules/state-overlay";
import Popin from "@/containers/popin-adaptive";
import LoadingSpinner from "@/components/molecules/loading-spinner";
import PageNotFoundProspectus from "@/components/pages/page-not-found-prospectus";
import PageNotFoundProduits from "@/containers/page-not-found-prospectus-produits";
import Button from "@/components/atoms/button";
import Titre from "@/components/atoms/titre";
import LazyImage from "@/components/atoms/lazy-image";
import { TrackJS } from "trackjs";
import { useGtm } from "@gtm-support/vue-gtm";
import HorairesMagasin from "@/components/organisms/horaires-magasin";

export default {
  components: {
    Popin,
    LoadingSpinner,
    PageNotFoundProspectus,
    PageNotFoundProduits,
    Button,
    LazyImage,
    Titre,
    StateOverlay,
    HorairesMagasin
  },
  props: {
    url: {
      type: String,
      required: true
    },
    page: {
      type: Number,
      required: false,
      default: 1
    },
    borne: {
      type: Boolean,
      required: false,
      default: false
    },
    prospectus: {
      type: Object,
      required: false,
      default: null
    },
    iframeHeight: {
      type: Number,
      required: false,
      default: 800
    }
  },
  computed: {
    ...mapState(useMagasinStore, {
      shopId: "id",
      codeMagasin: "codeMagasin",
      magasinName: "name",
      isNational: "isNational",
      magasin: "magasin"
    }),
    ...mapState(useContextStore, ["layout"]),
    src() {
      return (
        this.baseUrl +
        this.url +
        "?store=" +
        this.codeMagasin +
        "&mode=iframe" +
        (this.borne ? "&borne=true" : "") +
        "&page=" +
        this.page
      );
    },
    component() {
      return defineAsyncComponent(
        lazyComponents[`/components/pages/product-sheet/${this.layout}.vue`]
      );
    },
    componentMaintenance() {
      return defineAsyncComponent(
        lazyComponents[`/components/pages/maintenance/${this.layout}.vue`]
      );
    }
  },
  data() {
    return {
      baseUrl: useRuntimeConfig().public.PROSPECTUS_URL,
      showPopin: false,
      loading: false,
      product: null,
      breadcrumb: null,
      reviews: null,
      statistics: null,
      productErrorData: null,
      isReplacedByNational: false,
      isProspectusLoading: true,
      status: null,
      isReady: false
    };
  },
  methods: {
    ...mapActions(useMagasinStore, {
      storeLocatorRedirectionUrl: "setStoreLocatorRedirectionUrl"
    }),
    ...mapActions(useMagasinStore, {
      displayOrCloseStoreLocatorPopin: "displayOrCloseStoreLocatorPopin"
    }),
    receiveMessage(event) {
      if (!event || !event.data) {
        return;
      }
      const gtm = useGtm();

      const req = event.data;
      if (req.type === "ficheProduit" && req.data && req.data.id) {
        const id = req.data.id;
        this.handleId(id);
      } else if (req.type === "pagination" && req.page) {
        const hasPage = this.$route.fullPath.indexOf("page=") > -1;
        const hasQuery = this.$route.fullPath.indexOf("?") > -1;
        let redirectUrl = this.$route.fullPath;
        if (hasPage) {
          redirectUrl = redirectUrl.replace(/page=[0-9]./g, "page=" + req.page);
        } else if (hasQuery) {
          redirectUrl = redirectUrl + "&page=" + req.page;
        } else {
          redirectUrl = redirectUrl + "?page=" + req.page;
        }
        this.storeLocatorRedirectionUrl(redirectUrl);
      } else if (req === "backProspectus") {
        navigateToInternal(this.borne ? "/prospectus/borne" : "/prospectus");
      } else if (req.type === "load") {
        this.isProspectusLoading = false;
        this.status = req.status;
      } else if (req.type === "horaires") {
        this.$refs.popinHoraires.show();
      } else if (req.type === "dataLayer") {
        gtm.trackEvent(req.data);
      }
    },
    async handleId(idProduit) {
      this.productErrorData = null;
      this.loading = true;
      this.showPopin = true;
      const productId = idProduit;
      const shopId = this.shopId;
      this.isReplacedByNational = false;
      try {
        const response = await this.$apiDrive
          .use(productInfoApiDrive)
          .productInfo({
            shopId,
            productId
          });
        this.product = response.data;
        this.breadcrumb = response.meta.breadcrumb;
      } catch (error) {
        if (error.response.data.status_code === 404) {
          try {
            const response = await this.$apiDrive
              .use(productInfoApiDrive)
              .productInfo({
                shopId: useRuntimeConfig().public.MAGASINS_NATIONAL,
                productId
              });
            this.product = response.data;
            this.breadcrumb = response.meta.breadcrumb;
            this.isReplacedByNational = true;
          } catch {}
        }
        if (!this.isReplacedByNational) {
          this.productErrorData = error.response.data;
          this.product = null;
          this.breadcrumb = null;
          this.loading = false;
          return;
        }
      }
      try {
        const response = await this.$apiDrive
          .use(productReviewsApiDrive)
          .reviews({
            shopId: this.isReplacedByNational
              ? useRuntimeConfig().public.MAGASINS_NATIONAL
              : this.shopId,
            productId,
            sortBy: avisVerifiesCodes.DEFAULT_SORT_METHOD,
            pageSize:
              this.layout === LayoutTypes.mobile
                ? avisVerifiesCodes.DEFAULT_PAGE_SIZE_MOBILE
                : avisVerifiesCodes.DEFAULT_PAGE_SIZE,
            pageIndex: avisVerifiesCodes.DEFAULT_PAGE_INDEX
          });
        this.reviews = response.data.attributes.reviews;
        this.statistics = response.meta.statistics;
      } catch (error) {
        this.reviews = null;
        this.statistics = null;
      }
      this.loading = false;
    },
    closePopin() {
      this.showPopin = false;
    },
    prospectusLoaded() {
      // timeout de 15s après le chargement de l'iframe au cas où l'event d'activeDocs ne soit pas remonté
      this.startTimeoutProspectusLoading(15000);
    },
    startTimeoutProspectusLoading(duration = 10000) {
      setTimeout(() => {
        this.isProspectusLoading = false;
        if (!this.status && !this.isNational) {
          this.status = 500;
          if (TrackJS.isInstalled()) {
            TrackJS.track(
              `Prospectus non affiché au client (timeout ${duration}ms)`
            );
          }
        }
      }, duration);
    }
  },
  mounted() {
    window.addEventListener("message", this.receiveMessage);
    // Empêche que l'iframe ne soit chargée avant le listener de messages
    this.isReady = true;
    if (process.env.NODE_ENV !== "production") {
      this.isProspectusLoading = false;
      this.status = 200;
      return;
    }
    // timeout global de 60s au cas où aucune iframe n'a jamais été chargée
    this.startTimeoutProspectusLoading(60000);
  },
  beforeUnmount() {
    window.removeEventListener("message", this.receiveMessage);
  }
};
</script>

<template>
  <div>
    <Popin
      ref="popinHoraires"
      size="sm"
    >
      <Titre
        tag="h1"
        variant="primary"
        size="md"
        class="u-mb-md u-text-center"
      >
        Horaires du magasin {{ magasin.attributes.magasin }}
      </Titre>
      <HorairesMagasin :magasin="magasin" />
    </Popin>
    <Popin
      :visible="showPopin"
      aria-label="Produit"
      size="xlg"
      @closed="closePopin"
    >
      <LoadingSpinner v-if="loading" />
      <template v-else>
        <PageNotFoundProduits
          v-if="productErrorData"
          @close="closePopin"
        />
        <component
          :is="component"
          v-else-if="product"
          :product="product"
          :push-zones="product.attributes.push_zones"
          :breadcrumb="breadcrumb"
          :reviews="reviews"
          :statistics="statistics"
          :shop-id="shopId"
          :back-button="false"
          :is-replaced-by-national="isReplacedByNational"
          from-prospectus
          is-popin
        />
      </template>
    </Popin>
    <div v-if="!isNational">
      <StateOverlay
        :is-loading="isProspectusLoading"
        overlay
        message="Nous chargeons votre prospectus"
      >
        <PageNotFoundProspectus
          v-if="status === 404 || status === 410"
          :magasin-name="magasinName"
          :status="status"
          :borne="borne"
        />
        <component
          :is="componentMaintenance"
          v-else-if="status === 500"
          is-temp
          :borne="borne"
        />
        <div
          v-else-if="!isReady"
          :style="`height: ${iframeHeight}px;`"
        ></div>
        <iframe
          v-else
          title="Prospectus"
          width="100%"
          :height="iframeHeight"
          :src="src"
          sandbox="allow-same-origin allow-scripts allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
          @load="prospectusLoaded()"
        >
        </iframe>
      </StateOverlay>
    </div>
    <div
      v-else
      class="u-text-center u-bgcolor-white u-p-md"
    >
      <template v-if="prospectus">
        <Titre
          tag="h1"
          variant="primary"
          size="lg"
          class="u-mb-md"
        >
          {{ prospectus.attributes.title }}
        </Titre>
        <LazyImage
          :src="prospectus.attributes.cover"
          :dimension="300"
        />
      </template>
      <div class="u-mb-md">
        <p class="u-mb-md u-mt-md">
          Merci de choisir un magasin afin de commander les produits de ce propectus sur cora.fr
        </p>
        <Button
          variant="primary"
          size="md"
          radius="md"
          @click="displayOrCloseStoreLocatorPopin()"
        >
          Choisir un magasin
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
